import dayjs from 'dayjs';
import type { jsPDF } from 'jspdf';
import type { RowInput } from 'jspdf-autotable';
import type { User } from 'oidc-client-ts';
import type { ScannedOrder } from '../../hooks/store/useScannedLabelsStore.ts';
import { SHORT_DATETIME_FORMAT } from '../../utils/dateFormatUtils.ts';
import { patientFullName } from '../../utils/patient.ts';
import { LIBERATION_SANS, registerLiberationSans } from './liberationFont.ts';

const addHeader = (doc: jsPDF) => {
  doc.setFont(LIBERATION_SANS, 'bold');
  doc.setFontSize(18);
  doc.text('Probenannahme Report', 14, 20);
};

const addFooter = (doc: jsPDF, author: string) => {
  const pageCount = doc.getNumberOfPages();
  doc.setFont(LIBERATION_SANS, 'normal');
  doc.setFontSize(8);
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.text(
      `Seite ${i} von ${pageCount}, generiert von ${author} am ${dayjs().format(SHORT_DATETIME_FORMAT)}`,
      doc.internal.pageSize.width / 2,
      287,
      {
        align: 'center',
      }
    );
  }
};

export const generateReport = async (
  scannedOrders: ScannedOrder[],
  scannedLabelIds: string[],
  user: User | null | undefined
): Promise<string> => {
  const title = 'Probenannahme Report';
  const author = `${user?.profile?.given_name} ${user?.profile?.family_name}`;
  // jsPDF + autotable are ~430 kB and only needed once the user asks for the report
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);

  const doc = new jsPDF({ format: 'a4', orientation: 'portrait' });
  await registerLiberationSans(doc);

  doc.setProperties({
    title: title,
    subject: title,
    author: author,
    keywords: title,
    creator: 'medcom GmbH',
  });

  addHeader(doc);

  autoTable(doc, {
    startY: 30,
    // autotable takes the cell font from its own defaults ('helvetica'), not from doc.setFont --
    // without this the patient and Zuweiser columns stay broken
    styles: { font: LIBERATION_SANS },
    headStyles: { fillColor: 'gray' },
    bodyStyles: { lineColor: '#cccccc' },
    head: [['', 'Auftrag', 'Zuweiser', 'Patient', 'Benötigt', 'Vorhanden']],
    body: scannedOrders.flatMap((order, index) => {
      const complete = order.orderLabels.length === order.availableLabels;
      const fillColor = complete ? '#f6ffed' : '#fff2f0';
      const lineWidth = index === 0 ? 0 : { top: 0.1 };
      const mainRow: RowInput = [
        {
          styles: { fillColor: complete ? '#52c41a' : '#ff4d4f', lineWidth: lineWidth },
          content: '',
        },
        { styles: { fillColor: fillColor, lineWidth: lineWidth }, content: order.number },
        {
          styles: { fillColor: fillColor, lineWidth: lineWidth, overflow: 'ellipsize' },
          content: order.request.doctor.name,
        },
        {
          styles: { fillColor: fillColor, lineWidth: lineWidth, overflow: 'ellipsize' },
          content: patientFullName(order.request.patientData),
        },
        {
          styles: { fillColor: fillColor, lineWidth: lineWidth },
          content: order.orderLabels.length,
        },
        { styles: { fillColor: fillColor, lineWidth: lineWidth }, content: order.availableLabels },
      ];
      return complete
        ? [mainRow]
        : [
            mainRow,
            [
              {
                styles: { fillColor: fillColor, textColor: '#cf1322', lineWidth: { top: 0 } },
                content: order.orderLabels
                  .filter(it => !scannedLabelIds.includes(it.id))
                  .map(it => it.name)
                  .join(', '),
                colSpan: 6,
              },
            ],
          ];
    }),
  });

  addFooter(doc, author);
  return doc.output('bloburl').toString();
};
